import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  Link01Icon, 
  ArrowRight01Icon, 
  Shield02Icon,
  InformationCircleIcon
} from '@hugeicons/core-free-icons';
import Button from '../components/Button';
import { api } from '../utils/api';
import { FLAGS } from '../config/flags';
import { useAuth } from '../contexts/AuthContext';

const DerivLinking = () => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // If user is already linked, redirect away from this page
  useEffect(() => {
    if (user?.deriv_linked) {
      if (user.role === 'agent') {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleLinkDeriv = async () => {
    setLoading(true);
    
    if (FLAGS.USE_MOCK_API) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      window.location.href = `${window.location.origin}/?deriv_mock_success=true`;
      return;
    }

    try {
      const response = await api.get('/deriv/authorize-url');
      if (response.data && response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (err) {
      alert(err.message || "Failed to initiate Deriv linking. Please try again.");
      setLoading(false);
    }
  };

  // If we're waiting for user object or about to redirect, show loader
  if (!user || user.deriv_linked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-soft-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-off-white dark:bg-dark-bg">
      <div className="max-w-xl w-full bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-lg text-center">
        <div className="bg-soft-green/10 p-6 rounded-[2rem] inline-block mb-8 text-soft-green animate-float">
          <HugeiconsIcon icon={Link01Icon} size={48} />
        </div>
        
        <h1 className="text-4xl font-black text-charcoal dark:text-white mb-4 tracking-tight">Link Deriv Account</h1>
        <p className="text-muted-gray text-lg mb-8 leading-relaxed">
          To start trading on Safzi, you need to securely connect your Deriv account. This allows us to automate your funding process.
        </p>

        <div className="space-y-4 mb-8 text-left">
          <div className="flex items-start gap-4 p-4 bg-off-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700">
            <div className="bg-soft-green/10 p-2 rounded-xl text-soft-green">
              <HugeiconsIcon icon={Shield02Icon} size={20} />
            </div>
            <div>
              <p className="font-bold text-charcoal dark:text-white text-sm">Secure Connection</p>
              <p className="text-xs text-muted-gray">We use official OAuth 2.0. We never see your password.</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-4 bg-off-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700">
            <div className="bg-blue-500/10 p-2 rounded-xl text-blue-500">
              <HugeiconsIcon icon={InformationCircleIcon} size={20} />
            </div>
            <div>
              <p className="font-bold text-charcoal dark:text-white text-sm">Automated Funding</p>
              <p className="text-xs text-muted-gray">Once linked, deposits and withdrawals are processed instantly.</p>
            </div>
          </div>
        </div>

        <Button 
          onClick={handleLinkDeriv} 
          disabled={loading}
          className="w-full py-5 rounded-2xl text-xl font-black shadow-float-lg gap-2"
        >
          {loading ? 'Redirecting to Deriv...' : (
            <>
              Link My Account
              <HugeiconsIcon icon={ArrowRight01Icon} size={24} />
            </>
          )}
        </Button>
        
        <p className="mt-6 text-xs text-muted-gray">
          You will be redirected to the secure Deriv login page.
        </p>
      </div>
    </div>
  );
};

export default DerivLinking;